import { AIError, SYSTEM_PROMPT } from './types';

const BASE_URL = 'https://generativelanguage.googleapis.com/v1beta';
export const GEMINI_TEXT_MODEL = 'gemini-2.5-flash';

const headersFor = (apiKey: string) => ({
  'Content-Type': 'application/json',
  'x-goog-api-key': apiKey,
});

const errorFrom = async (response: { statusText: string; json: () => Promise<any> }): Promise<AIError> => {
  let message = response.statusText;
  try {
    const data = await response.json();
    message = data?.error?.message || message;
  } catch {
    // body was not JSON; keep statusText
  }
  return new AIError('gemini', `Gemini error: ${message || 'request failed'}`);
};

export const geminiText = async (prompt: string, apiKey: string): Promise<string> => {
  const response = await fetch(`${BASE_URL}/models/${GEMINI_TEXT_MODEL}:generateContent`, {
    method: 'POST',
    headers: headersFor(apiKey),
    body: JSON.stringify({
      system_instruction: { parts: [{ text: SYSTEM_PROMPT }] },
      contents: [{ parts: [{ text: prompt }] }],
      generationConfig: {
        temperature: 0.7,
        thinkingConfig: { thinkingBudget: 0 },
      },
    }),
  });

  if (!response.ok) {
    throw await errorFrom(response);
  }

  const data = await response.json();
  const parts: any[] = data?.candidates?.[0]?.content?.parts || [];
  const textPart = parts.find(p => !p.thought && p.text);
  if (textPart) return textPart.text;

  throw new AIError('gemini', 'Gemini error: no response text');
};

export const testGeminiKey = async (apiKey: string): Promise<void> => {
  const response = await fetch(`${BASE_URL}/models?pageSize=1`, { headers: headersFor(apiKey) });
  if (!response.ok) {
    throw await errorFrom(response);
  }
};

export const GEMINI_IMAGE_MODEL = 'gemini-3.1-flash-image';

export const extractImage = (json: unknown): { mimeType: string; data: string } | null => {
  if (Array.isArray(json)) {
    for (const item of json) {
      const found = extractImage(item);
      if (found) return found;
    }
    return null;
  }
  if (json === null || typeof json !== 'object') return null;

  const obj = json as Record<string, unknown>;
  const mime = obj.mime_type ?? obj.mimeType;
  if (typeof obj.data === 'string' && typeof mime === 'string' && mime.startsWith('image/')) {
    return { mimeType: mime, data: obj.data };
  }
  for (const value of Object.values(obj)) {
    const found = extractImage(value);
    if (found) return found;
  }
  return null;
};

export const base64ToBlob = (data: string, mimeType: string): Blob => {
  const binary = atob(data);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], { type: mimeType });
};

export const geminiImage = async (prompt: string, apiKey: string): Promise<Blob> => {
  const response = await fetch(`${BASE_URL}/interactions`, {
    method: 'POST',
    headers: headersFor(apiKey),
    body: JSON.stringify({
      model: GEMINI_IMAGE_MODEL,
      input: [{ type: 'text', text: prompt }],
      response_format: { type: 'image', mime_type: 'image/jpeg', aspect_ratio: '1:1' },
    }),
  });

  if (!response.ok) {
    throw await errorFrom(response);
  }

  const image = extractImage(await response.json());
  if (!image) {
    throw new AIError('gemini', 'Gemini returned no image. Try again or change the prompt.', true);
  }
  return base64ToBlob(image.data, image.mimeType);
};
